// Adding and removing tiles: Add Shortcut (a placeholder, then the Edit dialog), adding a widget, Duplicate, Delete
// (with the tiles inside a group), copying tiles and pasting them or a link onto the webtop, and the Add / Edit
// Shortcut dialog itself (its Opens field takes an address, /@webtop or @applet/command, with suggestions), and adding
// many tiles at once (addTiles: an import, the Webtops applet's Import through @webtop/api's webtop.addTiles).
import { WIDGETS } from '../widgets/index.js';
import { commandSuggestions, LAUNCHER, putAsset, withService } from './applets.js';
import { editField, SCALE } from '../widgets/kit.js';
import { EDGE, GRID, pageX, pinDrawn, place, reading, screenW, scrolled, settle, sizeOf, snap, snapOn, spotFields, TILE, TILE_GAP, tilePos, viewportOn } from './geometry.js';
import { $menu } from './menu.js';
import { setGroupItems } from './groups.js';
import { webtopLists } from './notch.js';
import { render, selectedShortcuts, setSelection } from './render.js';
import { API, byId, currentRole, $webtop, webtop, setWebtop } from './state.js';
import {
  allMembers, classifyTarget, firstGlyph, forgetDroppedIcons, groupItems, groupLinks, iconNode, isGroup, isImage,
  onWebtop, openShortcut, parentGroup, PLACEHOLDER_ICONS, targetText, tileIcon,
} from './tiles.js';
import { suggest } from './suggest.js';
import { api, confirmDialog, modal, toast, TYPING } from './ui.js';
import { webtopHref } from '/model.js';

const $dlg = document.getElementById('dlg');
const $form = document.getElementById('shortcut-form');

// Create and save a placeholder right where the menu was opened, then open the editor on it.
export async function addShortcutAt(at) {
  if (viewportOn()) at = settle(at, drawnRects(), TILE, !snapOn());
  try {
    await keepDrawn();
    const created = await api('POST', `${API}/shortcuts`, { title: 'New Shortcut', icon: '', kind: 'url', target: '', ...spotFields(at) });
    webtop.shortcuts.push(created);
    setSelection([created.id]);
    render();
    $webtop.querySelector(`.tile[data-id="${created.id}"]`)?.focus();
    openDialog(created); // fill in details now; Cancel keeps the placeholder (badge shows it needs a link)
  } catch (e) { toast(e); }
}

// A tile as Copy and Duplicate hand it on: its record without its id, a group's with its `members` the same way,
// subgroups and all.
const strip = ({ id, added, ...fields }) => fields; // a copy is a new tile, with a new id and date added
const snapshot = (s) => ({ ...strip(s), ...(isGroup(s) ? { members: groupItems(s).map(snapshot) } : {}) });

// Saves copies of `snaps` (as snapshot() makes them, or as pasted) in one import, each with `extras[i]` (its spot),
// and answers the new ids in the same order (null for one not saved). Every copy has a temporary id that a copied
// group's items refer to, and the server answers with the new ids in the order sent: one request however many
// tiles, where a request per tile was slow for big groups. A group with nothing in it is not copied.
async function saveCopies(snaps, extras = []) {
  const copies = [];
  const members = (t) => (Array.isArray(t.members) ? t.members.filter((m) => m && typeof m === 'object') : []);
  const worth = (t) => !isGroup(t) || members(t).some(worth);
  const add = (t, extra = {}) => {
    const { members: _, ...fields } = strip(t);
    const c = { ...fields, ...extra, id: `copy${copies.length}` };
    copies.push(c);
    if (isGroup(t)) c.data = { ...t.data, items: members(t).filter(worth).map((m) => add(m)) };
    return c.id;
  };
  const tops = snaps.map((t, i) => (worth(t) ? add(t, extras[i]) : null));
  if (!copies.length) return tops;
  const { imported, ...next } = await api('POST', `${API}/import`, { shortcuts: copies, mode: 'append' });
  setWebtop(next);
  const real = new Map(copies.map((c, i) => [c.id, imported[i]]));
  return tops.map((tmp) => (tmp ? real.get(tmp) : null));
}

// The rects of the tiles on the webtop as drawn, for place().
const drawnRects = () => [...$webtop.querySelectorAll('.tile')]
  .map((t) => ({ ...tilePos(t), ...sizeOf(byId(t.dataset.id)) }));
// Under Viewport a new tile comes with the others held where they are drawn (pinDrawn); saved first, since what creates
// a tile may answer the whole record.
async function keepDrawn() {
  const pins = pinDrawn();
  if (pins.length) await api('PATCH', `${API}/shortcuts`, pins);
}

// Duplicate: a copy of each tile (title, icon, size, its own data, ...), saved in the first free spot to the right of
// the original as drawn, as a drop there would find it. A copied group gets copies of its members, subgroups and
// all (a tile is in one group at most), and a copy of a tile inside a group joins that group right after the
// original. The copies end up selected, so they can be dragged away at once.
export async function duplicateTiles(list) {
  const taken = drawnRects();
  const sorted = [...list].sort(reading);
  const spots = sorted.map((s) => {
    if (parentGroup(s)) return {}; // its copy goes into the group, not on the webtop
    const size = sizeOf(s);
    const el = $webtop.querySelector(`.tile[data-id="${s.id}"]`);
    const from = el ? tilePos(el) : s;
    const at = viewportOn()
      ? settle(snap(from.x + size.w + GRID.w - TILE.w, from.y, size, false, true), taken, size)
      : place(snap(from.x + size.w + GRID.w - TILE.w, from.y, size), taken, size, Boolean(WIDGETS[s.kind]));
    taken.push({ ...at, ...size });
    return spotFields(at, s);
  });
  try {
    await keepDrawn();
    const inGroups = sorted.map((s) => parentGroup(s)?.id); // before the webtop is replaced by the answer
    const ids = await saveCopies(sorted.map(snapshot), spots);
    const shown = [];
    for (const [i, s] of sorted.entries()) {
      if (!ids[i]) continue;
      const g = inGroups[i] && byId(inGroups[i]);
      if (!g) { shown.push(byId(ids[i])); continue; }
      const items = [...g.data.items];
      items.splice(items.indexOf(s.id) + 1, 0, ids[i]);
      await setGroupItems(g, items);
    }
    if (shown.length) setSelection(shown.map((c) => c.id));
    render(shown); // the copies keep the spots found for them
  } catch (e) { toast(e); }
}

/**
 * Adds `tiles` (exported tiles, or new ones in the shape newShortcut takes) to the open webtop through its import
 * route: `mode` 'append' (beside what is there) or 'replace' (instead of it). `place`, ids among `tiles` in order, are
 * laid out on the grid in that order (below what is already there when appending): tiles that came without spots of
 * their own (bookmarks). Answers the new tiles' ids, in the order they were sent.
 */
export async function addTiles(tiles, { mode = 'append', place: order = null } = {}) {
  if (!Array.isArray(tiles)) throw new Error('addTiles needs a list of tiles');
  const list = structuredClone(tiles);
  if (Array.isArray(order) && order.length) {
    const cols = Math.max(1, Math.floor((screenW() - 2 * EDGE + TILE_GAP) / GRID.w));
    const bottom = mode === 'append' ? Math.max(0, ...onWebtop().map((s) => s.y + sizeOf(s).h + TILE_GAP - EDGE)) : 0;
    const row0 = Math.ceil(bottom / GRID.h);
    const byTmp = new Map(list.map((t) => [t.id, t]));
    order.forEach((id, i) => {
      const t = byTmp.get(id);
      if (t) Object.assign(t, { x: EDGE + (i % cols) * GRID.w, y: EDGE + (row0 + Math.floor(i / cols)) * GRID.h });
    });
  }
  const before = webtop.shortcuts;
  const { imported, ...next } = await api('POST', `${API}/import`, { shortcuts: list, mode: mode === 'replace' ? 'replace' : 'append' });
  forgetDroppedIcons(before, next.shortcuts); // a replace drops the tiles that were there
  setWebtop(next);
  setSelection([]);
  render();
  return imported;
}

// `ask: false` skips the confirmation (a drop on the notch's trash, which is a deliberate gesture of its own) and
// says what went in a toast instead.
export async function removeShortcuts(list, { ask = true } = {}) {
  const one = list.length === 1;
  const name = one ? `“${list[0].title || 'Group'}”` : `${list.length} tiles`;
  // A group goes with the tiles in it, subgroups and all.
  const inside = list.filter(isGroup).flatMap((g) => allMembers(g));
  if (ask && !await confirmDialog({
    title: `Delete ${name}?`,
    text: (inside.length ? `The ${inside.length} tile${inside.length === 1 ? '' : 's'} in it go too. ` : '')
      + `${one ? 'It' : 'They'} will be removed from this webtop. This cannot be undone.`,
    button: 'Delete', danger: true,
  })) return;
  // One request for all of them: the server also takes what is inside the groups and any group left empty
  // (store.removeTiles), and answers with the webtop as it is now.
  try {
    const before = webtop.shortcuts;
    const next = await api('DELETE', `${API}/shortcuts`, { ids: list.map((s) => s.id) });
    forgetDroppedIcons(before, next.shortcuts); // what went, groups' members included
    setWebtop(next);
    if (!ask) toast(`Deleted ${name}`);
  } catch (e) { toast(e); }
  setSelection([]);
  render();
}

// Copy (Cmd / Ctrl+C) on the webtop: the selected tiles, or the focused one, go on the clipboard as their records
// (a group with its members) under CLIP_TYPE, and as plain text too: their addresses, or titles for tiles without
// one, a line each, so a copy pasted elsewhere is still useful. Paste (Cmd / Ctrl+V) on an unlocked webtop, this one
// or another in the same browser, saves copies of them; a clipboard holding one http(s) address instead becomes a
// shortcut to it, titled with the address. Either lands centred on the mouse pointer (as a drop there would place
// it: the nearest free spot), or, without a pointer over the page, at the copied spots (the nearest free ones) or the
// first free spot for a link. The copy and paste events carry the clipboard themselves, so this needs no clipboard
// permission and works on the plain-HTTP LAN address too (navigator.clipboard would not). Copies and pastes in a
// text field, over selected text, or with a menu, dialog, overlay or group panel open, are left alone.
const CLIP_TYPE = 'application/x-webtop+json';
const CLIP_MAX = 100; // tiles in one paste, members of a group not counted
let lastCopy = null; // {text, tiles}: what this page copied, for a browser that drops CLIP_TYPE on the way

function pastedLink(text) {
  const v = (text ?? '').trim();
  if (!/^https?:\/\/\S+$/i.test(v)) return null;
  try { new URL(v); return v; } catch { return null; }
}

function pastedTiles(e) {
  let tiles = null;
  try { tiles = JSON.parse(e.clipboardData?.getData(CLIP_TYPE) || 'null'); } catch { /* not ours */ }
  const text = e.clipboardData?.getData('text/plain');
  if (!Array.isArray(tiles) && lastCopy && text === lastCopy.text) tiles = lastCopy.tiles;
  if (!Array.isArray(tiles)) return null;
  tiles = tiles.filter((t) => t && typeof t === 'object' && typeof t.kind === 'string').slice(0, CLIP_MAX);
  return tiles.length ? tiles : null;
}

let pointer = null; // the mouse's last spot in the window, in unscaled px (the page may scroll before the paste)

// Where a tile wanted at `want` (unscaled px on the webtop) goes, as a drop there would place it.
function dropSpot(want, size, taken, widget = false) {
  const at = snap(want.x, want.y, size, false, true);
  return viewportOn() ? settle(at, taken, size, !snapOn()) : place(at, taken, size, widget, null, !snapOn());
}

async function addPastedLink(url) {
  const taken = drawnRects();
  const at = pointer
    ? dropSpot({ x: pointer.x - TILE.w / 2, y: pointer.y + scrolled() - TILE.h / 2 }, TILE, taken)
    : place({ x: 0, y: 0 }, taken, TILE);
  try {
    await keepDrawn();
    const created = await api('POST', `${API}/shortcuts`, { title: url, icon: '', kind: 'url', target: url, ...spotFields(at) });
    webtop.shortcuts.push(created);
    setSelection([created.id]);
    render([created]); // it keeps the spot found for it
    $webtop.querySelector(`.tile[data-id="${created.id}"]`)?.scrollIntoView({ block: 'nearest' });
    toast('Added a shortcut from the clipboard');
  } catch (e) { toast(e); }
}

// The copied tiles keep their arrangement: the block they make is centred on the pointer, each tile then placed as a
// drop on its own spot would be (so copies pasted on their own webtop without a pointer land next to the originals).
async function pasteTiles(tiles) {
  const taken = drawnRects();
  const sized = tiles.map((t) => ({ t, size: sizeOf(t), x: Number(t.x) || 0, y: Number(t.y) || 0 })).sort(reading);
  let dx = 0, dy = 0;
  if (pointer) {
    const left = Math.min(...sized.map((c) => c.x)), top = Math.min(...sized.map((c) => c.y));
    const right = Math.max(...sized.map((c) => c.x + c.size.w)), bottom = Math.max(...sized.map((c) => c.y + c.size.h));
    dx = pointer.x - (left + right) / 2;
    dy = pointer.y + scrolled() - (top + bottom) / 2;
  }
  const spots = sized.map(({ t, size, x, y }) => {
    const at = dropSpot({ x: x + dx, y: y + dy }, size, taken, Boolean(WIDGETS[t.kind]));
    taken.push({ ...at, ...size });
    return spotFields(at, t);
  });
  toast(`Pasting ${tiles.length === 1 ? 'a tile' : `${tiles.length} tiles`}…`);
  let shown = [];
  try {
    await keepDrawn();
    shown = (await saveCopies(sized.map((c) => c.t), spots)).map((id) => id && byId(id)).filter(Boolean);
  } catch (e) { return toast(e); }
  if (shown.length) setSelection(shown.map((c) => c.id));
  render(shown); // the copies keep the spots found for them
  if (!shown.length) return toast('Nothing to paste', { kind: 'info' });
  $webtop.querySelector(`.tile[data-id="${shown[0].id}"]`)?.scrollIntoView({ block: 'nearest' });
  toast(`Pasted ${shown.length === 1 ? 'a tile' : `${shown.length} tiles`}`);
}

// A copy or paste the webtop should leave to the browser.
const notOurs = (e) => e.defaultPrevented || e.target.closest?.(TYPING) || !$menu.hidden
  || document.querySelector('dialog[open], .launcher:not([hidden]), #group-panel:not([hidden])');

function onCopy(e) {
  if (notOurs(e) || getSelection()?.toString()) return;
  const focused = byId(document.activeElement?.closest?.('#webtop .tile')?.dataset.id);
  let list = selectedShortcuts();
  if (focused && !list.some((s) => s.id === focused.id)) list = [focused];
  if (!list.length) return;
  const tiles = [...list].sort(reading).map(snapshot);
  const text = list.map((s) => (s.kind === 'url' && s.target) || s.title || '').filter(Boolean).join('\n')
    || `${list.length} webtop tile${list.length === 1 ? '' : 's'}`;
  e.preventDefault();
  e.clipboardData.setData(CLIP_TYPE, JSON.stringify(tiles));
  e.clipboardData.setData('text/plain', text);
  lastCopy = { text, tiles };
  toast(`Copied ${list.length === 1 ? `“${list[0].title || 'Group'}”` : `${list.length} tiles`}`);
}

function onPaste(e) {
  if (notOurs(e)) return;
  const tiles = pastedTiles(e);
  const url = !tiles && pastedLink(e.clipboardData?.getData('text/plain'));
  if (!tiles && !url) return;
  e.preventDefault();
  if (currentRole() !== 'editor') {
    return toast(`Unlock this webtop to add ${tiles ? 'tiles' : 'a shortcut'}`, { kind: 'info' });
  }
  if (tiles) pasteTiles(tiles);
  else addPastedLink(url);
}

const F = $form.elements;
const $err = document.getElementById('form-error');
const $preview = document.getElementById('icon-preview');
const $iconFile = document.getElementById('icon-file');
let editing = null;
let autoTitle = null; // the title a picked suggestion filled in, replaced by the next pick (not if typed over)

// The preview shows the icon as the tile will: an empty field means the site's own icon (shown once the address
// is complete, on change, so half-typed addresses are not looked up).
function showPreview() {
  const { kind, target } = classifyTarget(F.opens.value);
  $preview.replaceChildren(...iconNode(tileIcon({ kind, target, icon: F.icon.value.trim() }), 'icon-preview').childNodes);
}

export async function openDialog(s) {
  editing = s;
  document.getElementById('dlg-title').textContent = s.title === 'New Shortcut' && !s.target ? 'Add Shortcut' : 'Edit Shortcut';
  $form.reset();
  $err.hidden = true;
  F.title.value = s.title ?? '';
  autoTitle = null;
  F.icon.value = PLACEHOLDER_ICONS.includes(s.icon) ? '' : (s.icon ?? '');
  F.opens.value = targetText(s);
  F.opens.placeholder = 'example.com,  /@work  or  @applet';
  F.icon.dispatchEvent(new Event('input'));
  $dlg.showModal();
  // a new shortcut starts at what it opens (its suggestions then name it); an existing one at its name
  const first = s.target ? F.title : F.opens;
  first.focus();
  first.select();
}
function showErr(msg) { $err.textContent = msg; $err.hidden = false; }
// What the Opens field suggests for what is typed: the applets, and the commands of the one named (client/lib/applets.js),
// then the favorite and recent webtops.
function opensSuggestions(text) {
  const { favorites, recent } = webtopLists();
  const webtops = [...favorites, ...recent].map((r) => ({ value: webtopHref(r.slug), label: r.title, title: r.title }));
  return [...commandSuggestions(text), ...webtops];
}
// The preview is also a button: the file picked becomes an asset of the webtop (putAsset, the storage applet's: the server's
// store, or the client storage of a webtop kept in this browser), and its address the icon, written by Save with the
// rest (an upload never saved is deleted by the server after a while, as every asset handed out and not written in).
async function uploadIcon(file) {
  if (!file.type.startsWith('image/')) return showErr('Pick an image file');
  $err.hidden = true;
  $preview.classList.add('busy');
  try {
    const { url } = await putAsset(file);
    F.icon.value = url;
    F.icon.dispatchEvent(new Event('input'));
  } catch (e) { showErr(e.message); } finally { $preview.classList.remove('busy'); }
}
// A picked suggestion names the shortcut, unless it already has a name of the user's own.
function pickedOpens(entry) {
  const title = F.title.value.trim();
  if (!entry.title || (title && title !== 'New Shortcut' && title !== autoTitle)) return;
  F.title.value = autoTitle = entry.title;
}

// Adds a widget of this kind with its defaults at (x, y) in unscaled px on the webtop (picked in the Add section,
// client/lib/add.js): its top-left corner there, or its middle with `centre`. It is kept on the part of the webtop in
// view (a scrolled webtop keeps it where it was clicked, not on the first screen) and the layout moves it only if
// that spot is taken.
// `extra` adds to the new tile (an applet widget's data and size, client/lib/add.js).
export async function addWidgetAt(kind, { x, y, centre = false }, extra = {}) {
  const w = WIDGETS[kind];
  const fields = { kind, size: w.sizes[0], ...w.create(), ...extra };
  const size = sizeOf({ kind, size: fields.size, data: fields.data });
  const wanted = centre ? snap(x - size.w / 2, y - size.h / 2, size, false, true) : snap(x, y, size, false, true);
  const at = viewportOn() ? settle(wanted, drawnRects(), size, !snapOn()) : wanted;
  try {
    await keepDrawn();
    const created = await api('POST', `${API}/shortcuts`, { ...fields, ...spotFields(at) });
    webtop.shortcuts.push(created);
    setSelection([created.id]);
    render([created]);
    const ta = $webtop.querySelector(`.tile[data-id="${created.id}"] textarea`);
    if (ta) editField(ta); // a new note is ready to type into
  } catch (e) { toast(e); }
}

// Event wiring and first drawing, run once by client.js in the order the parts of the page depend on.
// The launcher's Webtop Shortcuts column (the Launcher applet draws it): this webtop's links in the order they read on
// it, tiles by their spot, a group's links (subgroups included) where the group is, at most LINKS_MAX, matched by title
// and address; picking one opens it like a tap on its tile (a link in a new tab, a webtop link in this one).
const LINKS_MAX = 12; // four rows of three
function launcherLinks() {
  if (!webtop?.shortcuts) return []; // a locked private webtop: nothing loaded
  return onWebtop().sort(reading).flatMap((s) => (isGroup(s) ? groupLinks(s) : [s]))
    .filter((s) => ['url', 'webtop', 'command'].includes(s.kind))
    .map((s) => ({
      text: `${s.title}\n${targetText(s)}`, label: s.title, title: `Open ${targetText(s)}`, cls: 'link',
      glyph: () => iconNode(tileIcon(s), 'glyph'), action: () => openShortcut(s),
    }));
}

export function init() {
  withService(LAUNCHER, (l) => l.addColumn({ id: 'shortcuts', title: 'Webtop Shortcuts', order: 10, max: LINKS_MAX, across: 3, items: launcherLinks }));
  F.icon.addEventListener('input', showPreview);
  $preview.addEventListener('click', () => $iconFile.click());
  $iconFile.addEventListener('change', () => {
    const f = $iconFile.files[0];
    $iconFile.value = '';
    if (f) uploadIcon(f);
  });
  F.opens.addEventListener('change', showPreview);
  suggest(F.opens, opensSuggestions, pickedOpens);

  modal($dlg);
  addEventListener('copy', onCopy);
  addEventListener('paste', onPaste);
  addEventListener('pointermove', (e) => {
    if (e.pointerType === 'mouse') pointer = { x: pageX(e.clientX), y: e.clientY / SCALE };
  }, { passive: true });
  document.documentElement.addEventListener('mouseleave', () => { pointer = null; });

  $form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const title = F.title.value.trim();
    if (!title) return showErr('Title is required');
    const { kind, target, error } = classifyTarget(F.opens.value);
    if (error) return showErr(error);
    let icon = F.icon.value.trim();
    if (icon && !/^https?:\/\//i.test(icon) && !isImage(icon)) icon = firstGlyph(icon);
    try {
      // An icon given as a URL is stored as that URL: tiles show it through webtop's icon proxy, kept in this
      // browser's icon cache (client/lib/icons.js), so the server saves nothing.
      const body = { title, icon, kind, target }; // an empty icon: the site's icon, or webtop's logo
      const updated = await api('PATCH', `${API}/shortcuts/${editing.id}`, body);
      const before = { ...editing };
      Object.assign(editing, updated);
      forgetDroppedIcons([before], [editing]); // a new address or icon: the old one goes
      $dlg.close();
      render();
      $webtop.querySelector(`.tile[data-id="${editing.id}"]`)?.focus();
    } catch (err) { showErr(err.message); }
  });
}
